<template>
  <div class="faq-page">
    <div class="page-header">
      <div>
        <h1>Perguntas Frequentes (FAQ)</h1>
        <p>
          Base de conhecimento para consultas futuras dos usuários, apoio em treinamentos
          e padronização das orientações do sistema.
        </p>
      </div>

      <div class="header-badge">
        <span>{{ faqsFiltradas.length }}</span>
        <small>itens encontrados</small>
      </div>
    </div>

    <div class="faq-toolbar">
      <div class="toolbar-field search-field">
        <label>Pesquisar</label>
        <input
          v-model="filtros.termo"
          type="text"
          placeholder="Digite palavra-chave, pergunta ou assunto..."
        />
      </div>

      <div class="toolbar-field">
        <label>Categoria</label>
        <select v-model="filtros.categoria">
          <option value="">Todas</option>
          <option v-for="categoria in categorias" :key="categoria" :value="categoria">
            {{ categoria }}
          </option>
        </select>
      </div>

      <div class="toolbar-field">
        <label>Perfil</label>
        <select v-model="filtros.perfil">
          <option value="">Todos</option>
          <option v-for="perfil in perfis" :key="perfil" :value="perfil">
            {{ perfil }}
          </option>
        </select>
      </div>

      <div class="toolbar-check">
        <label class="checkbox-inline">
          <input v-model="filtros.soTreinamento" type="checkbox" />
          Mostrar apenas conteúdos úteis em treinamento
        </label>
      </div>
    </div>

    <div class="faq-highlights">
      <div class="highlight-card">
        <strong>{{ totalTreinamento }}</strong>
        <span>itens voltados a treinamento</span>
      </div>
      <div class="highlight-card">
        <strong>{{ categorias.length }}</strong>
        <span>categorias cadastradas</span>
      </div>
      <div class="highlight-card">
        <strong>{{ perfis.length }}</strong>
        <span>perfis atendidos</span>
      </div>
    </div>

    <div class="faq-list">
      <div
        v-for="item in faqsFiltradas"
        :key="item.id"
        class="faq-item"
        :class="{ aberto: item.aberto, destaque: item.treinamento }"
      >
        <button class="faq-question" @click="toggleItem(item.id)">
          <div class="faq-question-left">
            <span class="faq-category">{{ item.categoria }}</span>
            <h3>{{ item.pergunta }}</h3>

            <div class="faq-meta">
              <span class="tag perfil">{{ item.perfil }}</span>
              <span v-if="item.treinamento" class="tag treinamento">Treinamento</span>
              <span v-if="item.popular" class="tag popular">Dúvida recorrente</span>
            </div>
          </div>

          <span class="faq-icon">{{ item.aberto ? '−' : '+' }}</span>
        </button>

        <div v-if="item.aberto" class="faq-answer">
          <p>{{ item.resposta }}</p>

          <div v-if="item.passos?.length" class="faq-steps">
            <h4>Passos orientativos</h4>
            <ol>
              <li v-for="(passo, idx) in item.passos" :key="idx">
                {{ passo }}
              </li>
            </ol>
          </div>

          <div v-if="item.modulos?.length" class="faq-related">
            <h4>Módulos relacionados</h4>
            <div class="chips">
              <span v-for="modulo in item.modulos" :key="modulo" class="chip">
                {{ modulo }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!faqsFiltradas.length" class="empty-state">
        <h3>Nenhum FAQ encontrado</h3>
        <p>
          Revise os filtros informados ou cadastre novas perguntas frequentes para ampliar
          a base de apoio aos usuários.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive } from 'vue'

const filtros = reactive({
  termo: '',
  categoria: '',
  perfil: '',
  soTreinamento: false
})

const faqs = reactive([
  {
    id: 1,
    categoria: 'Estabelecimentos',
    perfil: 'Supervisor',
    pergunta: 'Como iniciar o processo de registro de um novo estabelecimento no SIM?',
    resposta:
      'O processo normalmente inicia pelo protocolo de registro do estabelecimento, com preenchimento dos dados cadastrais, anexação da documentação exigida e vinculação do checklist correspondente. Depois disso, o processo pode seguir para análise e vistorias.',
    passos: [
      'Acessar o módulo de Protocolo de Registro de Estabelecimentos',
      'Informar os dados básicos do estabelecimento',
      'Anexar documentos obrigatórios',
      'Vincular checklist do processo',
      'Encaminhar para análise'
    ],
    modulos: ['Cadastro de Estabelecimentos', 'Itens de Processo', 'Documentos'],
    treinamento: true,
    popular: true,
    aberto: true
  },
  {
    id: 2,
    categoria: 'PAC',
    perfil: 'Estabelecimento',
    pergunta: 'O que são os Programas de Autocontrole (PAC) do estabelecimento?',
    resposta:
      'São registros e controles internos adotados pelo estabelecimento para demonstrar conformidade sanitária, operacional e documental. O sistema pode manter esses programas organizados por tipo, status, vigência e anexos vinculados.',
    passos: [
      'Cadastrar o programa de autocontrole',
      'Definir tipo e situação',
      'Anexar evidências ou documentos',
      'Acompanhar vigência e pendências'
    ],
    modulos: ['PAC do Estabelecimento', 'Anexos', 'Vigências'],
    treinamento: true,
    popular: true,
    aberto: false
  },
  {
    id: 3,
    categoria: 'Rótulos e Produtos',
    perfil: 'Supervisor',
    pergunta: 'Qual a diferença entre cadastro de produto e processo de rótulo?',
    resposta:
      'O cadastro de produto representa o item produzido ou comercializado pelo estabelecimento. Já o processo de rótulo trata do registro, alteração, análise ou cancelamento da apresentação comercial e documental vinculada ao produto.',
    passos: [
      'Cadastrar ou localizar o produto',
      'Abrir o processo do rótulo',
      'Informar situação do processo',
      'Anexar documentos e pareceres'
    ],
    modulos: ['Produtos', 'Rótulos', 'Processos'],
    treinamento: true,
    popular: false,
    aberto: false
  },
  {
    id: 4,
    categoria: 'Laudos e Assinaturas',
    perfil: 'Fiscal',
    pergunta: 'Como funciona a assinatura eletrônica de laudos e documentos?',
    resposta:
      'Após a geração do documento, o usuário pode confirmar sua identidade por senha e registrar a assinatura eletrônica, mantendo histórico, hash de validação e demais dados exigidos para rastreabilidade do documento no sistema.',
    passos: [
      'Gerar o documento ou laudo',
      'Solicitar validação do usuário',
      'Registrar assinatura eletrônica',
      'Gravar hash e dados de validação'
    ],
    modulos: ['Laudos', 'Assinaturas', 'Validação Pública'],
    treinamento: true,
    popular: true,
    aberto: false
  },
  {
    id: 5,
    categoria: 'Boletos e Financeiro',
    perfil: 'Administrativo',
    pergunta: 'É possível controlar emissão e baixa de boletos no sistema?',
    resposta:
      'Sim. O conceito previsto para o sistema contempla visualização de lançamentos, emissão, situação do boleto, baixas por retorno bancário e histórico de movimentações relacionadas a multas, taxas e serviços.',
    passos: [
      'Cadastrar o lançamento financeiro',
      'Gerar o boleto',
      'Acompanhar situação',
      'Baixar automaticamente ou manualmente'
    ],
    modulos: ['Boletos', 'Financeiro', 'Retornos'],
    treinamento: false,
    popular: false,
    aberto: false
  },
  {
    id: 6,
    categoria: 'Agenda Fiscal',
    perfil: 'Fiscal',
    pergunta: 'Como a agenda de fiscalizações é definida no sistema?',
    resposta:
      'A agenda pode ser montada com base em critérios cadastrais, processos pendentes, classificações de risco ou parametrizações específicas do consórcio/município, permitindo organizar as visitas e ações dos fiscais.',
    passos: [
      'Configurar critérios de agenda',
      'Relacionar riscos ou pendências',
      'Gerar compromissos ou visitas',
      'Executar e registrar resultados'
    ],
    modulos: ['Agenda Fiscal', 'Risco', 'Vistorias'],
    treinamento: true,
    popular: false,
    aberto: false
  },
  {
    id: 7,
    categoria: 'Processos Administrativos',
    perfil: 'Supervisor',
    pergunta: 'Onde consultar o histórico de um processo administrativo vinculado ao estabelecimento?',
    resposta:
      'O histórico deve ficar disponível no próprio processo, exibindo movimentações, pareceres, documentos anexados, mudanças de status e vínculos com o estabelecimento correspondente.',
    passos: [
      'Acessar o processo administrativo',
      'Abrir a aba de histórico',
      'Consultar movimentações e anexos',
      'Verificar responsáveis e datas'
    ],
    modulos: ['Processos Administrativos', 'Histórico', 'Anexos'],
    treinamento: true,
    popular: true,
    aberto: false
  },
  {
    id: 8,
    categoria: 'Treinamento e Suporte',
    perfil: 'Todos',
    pergunta: 'Quem pode acessar a base de FAQ do sistema?',
    resposta:
      'A base de perguntas frequentes deve estar disponível para todos os perfis, servindo como apoio em treinamentos, consultas rápidas e padronização de orientações aos usuários do portal e da equipe interna.',
    passos: [
      'Acessar o módulo de FAQ',
      'Pesquisar por palavra-chave',
      'Filtrar por categoria ou perfil',
      'Abrir a resposta desejada'
    ],
    modulos: ['FAQ', 'Treinamentos', 'Portal'],
    treinamento: true,
    popular: true,
    aberto: false
  }
])

const categorias = computed(() => {
  const lista = faqs.map(item => item.categoria)
  return [...new Set(lista)].sort((a, b) => a.localeCompare(b))
})

const perfis = computed(() => {
  const lista = faqs.map(item => item.perfil)
  return [...new Set(lista)].sort((a, b) => a.localeCompare(b))
})

const totalTreinamento = computed(() => {
  return faqs.filter(item => item.treinamento).length
})

const faqsFiltradas = computed(() => {
  const termo = filtros.termo.trim().toLowerCase()

  return faqs.filter(item => {
    const atendeCategoria = !filtros.categoria || item.categoria === filtros.categoria
    const atendePerfil =
      !filtros.perfil || item.perfil === filtros.perfil || item.perfil === 'Todos'
    const atendeTreinamento = !filtros.soTreinamento || item.treinamento

    const textoBusca = [
      item.pergunta,
      item.resposta,
      item.categoria,
      item.perfil,
      ...(item.modulos || [])
    ]
      .join(' ')
      .toLowerCase()

    const atendeTermo = !termo || textoBusca.includes(termo)

    return atendeCategoria && atendePerfil && atendeTreinamento && atendeTermo
  })
})

function toggleItem(id) {
  const item = faqs.find(faq => faq.id === id)
  if (item) item.aberto = !item.aberto
}
</script>

<style scoped>
.faq-page {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: #f6f8fb;
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #dfe6ee;
  border-radius: 14px;
  padding: 20px;
}

.page-header h1 {
  margin: 0 0 6px 0;
  font-size: 24px;
  color: #243447;
}

.page-header p {
  margin: 0;
  color: #5d6b79;
  line-height: 1.5;
  max-width: 900px;
}

.header-badge {
  min-width: 140px;
  background: linear-gradient(135deg, #1d4e89, #2563eb);
  color: #ffffff;
  border-radius: 14px;
  padding: 14px 18px;
  text-align: center;
}

.header-badge span {
  display: block;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.header-badge small {
  display: block;
  margin-top: 6px;
  opacity: 0.95;
}

.faq-toolbar {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 14px;
  background: #ffffff;
  border: 1px solid #dfe6ee;
  border-radius: 14px;
  padding: 16px;
}

.toolbar-field,
.toolbar-check {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.toolbar-field label,
.toolbar-check label {
  font-size: 13px;
  font-weight: 600;
  color: #425466;
}

.toolbar-field input,
.toolbar-field select {
  height: 42px;
  border: 1px solid #cfd8e3;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 14px;
  outline: none;
  background: #fff;
}

.toolbar-field input:focus,
.toolbar-field select:focus {
  border-color: #2563eb;
}

.toolbar-check {
  grid-column: 1 / -1;
}

.checkbox-inline {
  display: flex;
  align-items: center;
  gap: 10px;
}

.faq-highlights {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

.highlight-card {
  background: #ffffff;
  border: 1px solid #dfe6ee;
  border-radius: 14px;
  padding: 18px;
  text-align: center;
}

.highlight-card strong {
  display: block;
  font-size: 28px;
  color: #1d4e89;
  margin-bottom: 6px;
}

.highlight-card span {
  color: #5d6b79;
  font-size: 14px;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.faq-item {
  background: #ffffff;
  border: 1px solid #dfe6ee;
  border-radius: 14px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.faq-item.destaque {
  border-left: 5px solid #2563eb;
}

.faq-item.aberto {
  box-shadow: 0 8px 24px rgba(30, 41, 59, 0.08);
}

.faq-question {
  width: 100%;
  border: none;
  background: transparent;
  padding: 18px;
  text-align: left;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
}

.faq-question-left {
  flex: 1;
}

.faq-category {
  display: inline-block;
  margin-bottom: 8px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eef4ff;
  color: #1d4e89;
  font-size: 12px;
  font-weight: 700;
}

.faq-question h3 {
  margin: 0;
  font-size: 18px;
  color: #243447;
}

.faq-meta {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.tag.perfil {
  background: #f2f4f7;
  color: #475467;
}

.tag.treinamento {
  background: #e9f7ef;
  color: #1f7a46;
}

.tag.popular {
  background: #fff4e5;
  color: #b76e00;
}

.faq-icon {
  min-width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #243447;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  line-height: 1;
}

.faq-answer {
  padding: 0 18px 18px 18px;
  border-top: 1px solid #edf2f7;
}

.faq-answer p {
  margin: 16px 0;
  color: #425466;
  line-height: 1.6;
}

.faq-steps h4,
.faq-related h4 {
  margin: 16px 0 10px 0;
  font-size: 14px;
  color: #243447;
}

.faq-steps ol {
  margin: 0;
  padding-left: 18px;
  color: #425466;
  line-height: 1.6;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  background: #eff6ff;
  color: #1d4e89;
  border: 1px solid #cfe0ff;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.empty-state {
  background: #ffffff;
  border: 1px dashed #cfd8e3;
  border-radius: 14px;
  padding: 40px 20px;
  text-align: center;
}

.empty-state h3 {
  margin: 0 0 8px 0;
  color: #243447;
}

.empty-state p {
  margin: 0;
  color: #5d6b79;
}

@media (max-width: 980px) {
  .faq-toolbar,
  .faq-highlights {
    grid-template-columns: 1fr;
  }

  .page-header {
    flex-direction: column;
  }

  .header-badge {
    width: 100%;
  }
}
</style>